import AddIcon from "@mui/icons-material/Add";
import ArrowIcon from "@mui/icons-material/ArrowForward";
import ChevronIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { ReactNode, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { getStages } from "../../shared/British";
import { Stage } from "../../shared/CaseConfiguration";
import { CaseListElement } from "../../shared/CasesList";
import { PortfolioOverview } from "../../shared/PortfolioTypes";
import { relativeTime } from "../../utils/relativeTime";
import { Copyright } from "../Copyright";
import { gainFill, TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SectionLabel } from "../general/SectionLabel";
import { SplitBar } from "../general/SplitBar";
import { TonalBadge } from "../general/TonalBadge";
import { ImportCaseDialog } from "../ImportCaseDialog";
import { getClaimTypeChipStyle } from "../issues/claimTypeColors";
import { SectionHeading } from "../portfolio/PortfolioSection";
import { getStageColor } from "../stageColors";
import { NO_FUNDING, summarise } from "./homeSummary";
import { PortfolioArea } from "./PortfolioArea";

/** The start page is no wider than this, in px. */
const MAX_WIDTH = 1180;

/** How many claim types are listed; the rest are counted in a line. */
const CLAIM_TYPES_SHOWN = 5;

/** How far the bars of the funding are lightened from the primary colour. */
const FUNDING_LIGHTEN = 0.35;
const NO_FUNDING_LIGHTEN = 0.75;

const card = { borderRadius: 3 } as const;
const plainLink = { textDecoration: "none", color: "inherit" } as const;

const CASES_PATH = "/cases";

/** A count as a row: what is counted, a bar against the largest count, the count. */
const CountRow = ({
  label,
  count,
  most,
  color,
  barWidth = 90,
  barHeight = 8,
  to,
  title,
}: {
  label: ReactNode;
  count: number;
  most: number;
  color: string;
  barWidth?: number | string;
  barHeight?: number;
  /** Where the row leads to; a row without it is no link. */
  to?: string;
  title?: string;
}) => (
  <Box
    {...(to ? { component: Link, to } : {})}
    title={title}
    sx={{
      display: "grid",
      gridTemplateColumns: `minmax(0, 1fr) ${
        typeof barWidth === "number" ? `${barWidth}px` : barWidth
      } 20px`,
      columnGap: 1.25,
      alignItems: "center",
      ...plainLink,
      // nothing to show of a count of none but that it is none
      color: count > 0 ? "text.primary" : "text.disabled",
      ...(to && { borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }),
    }}
  >
    <Typography
      variant="body2"
      component="span"
      noWrap
      sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        height: barHeight,
        borderRadius: 999,
        bgcolor: "action.hover",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: most > 0 ? `${(count / most) * 100}%` : 0,
          height: "100%",
          bgcolor: color,
        }}
      />
    </Box>
    <Typography
      variant="body2"
      component="span"
      sx={{ textAlign: "right", fontWeight: 700, ...TABULAR_NUMERALS }}
    >
      {count}
    </Typography>
  </Box>
);

/**
 * The page a user starts on, in two areas apart: working on the cases (what
 * there is and where it stands, the cases changed last to carry on with, all
 * of it the list of cases, counted), and the portfolio (the cases taken
 * together). No amount of a single case is shown, since this is the page
 * others see over a shoulder.
 */
export const StartPage = ({
  cases,
  overview,
  userName,
  quote,
  now = new Date(),
}: {
  cases: CaseListElement[];
  /** The figures of the portfolio, or the promise of them. */
  overview: Promise<PortfolioOverview> | PortfolioOverview;
  userName?: string;
  /** One of the testimonials. */
  quote?: { text: string; author: string };
  now?: Date;
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t, i18n } = useTranslation(undefined, { keyPrefix: "home" });
  const navigate = useNavigate();
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState("");

  // the resolved language, so that the times read in the language of the page
  const locale = i18n.resolvedLanguage ?? "en";
  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    [locale],
  );
  const ago = (iso: string) => relativeTime(iso, rtf, now.getTime());

  const summary = useMemo(
    () => summarise(cases, getStages(), now),
    [cases, now],
  );
  const [latest] = summary.recent;
  const bold = (
    <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }} />
  );

  const hour = now.getHours();
  const greeting = t(
    `greeting.${hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening"}`,
    { name: userName },
  );

  const figures: {
    label: string;
    value: string;
    note: string;
    /** Where the figure leads to; a figure without it is no link. (Agent-written comment.) */
    to?: string;
  }[] = [
    {
      label: t("figures.cases"),
      value: String(summary.total),
      note: t("figures.changedToday", { count: summary.changedToday }),
      to: CASES_PATH,
    },
    {
      label: t("figures.settled"),
      value: String(summary.settled),
      note: t("figures.ofCases", { count: summary.total }),
      to: `${CASES_PATH}?settlement=Settled`,
    },
    {
      label: t("figures.open"),
      value: String(summary.open),
      note: t("figures.partial", { count: summary.partial }),
    },
    {
      label: t("figures.claimed"),
      value: currency.formatCompact(summary.claimed),
      note: t("figures.acrossAll"),
    },
  ];

  const mostPerStage = Math.max(0, ...summary.stages.map((s) => s.count));
  const shownTypes = summary.claimTypes.slice(0, CLAIM_TYPES_SHOWN);
  const moreTypes = summary.claimTypes.length - shownTypes.length;
  const mostFunding = Math.max(0, ...summary.funding.map((f) => f.count));

  const settlementParts = [
    {
      key: "none",
      count: summary.open - summary.partial,
      color: lighten(theme.palette.primary.main, NO_FUNDING_LIGHTEN),
      to: undefined,
    },
    {
      key: "partial",
      count: summary.partial,
      color: lighten(theme.palette.primary.main, FUNDING_LIGHTEN + 0.2),
      to: `${CASES_PATH}?settlement=Partial`,
    },
    {
      key: "full",
      count: summary.settled,
      color: gainFill(theme),
      to: `${CASES_PATH}?settlement=Settled`,
    },
  ].filter((part) => part.count > 0);

  const others = (c: CaseListElement) =>
    c.parties.filter((party) => party !== c.clientParty).join(" · ");

  return (
    <Box component="main" sx={{ px: 3.5, pt: 3.75, pb: 5 }}>
      <Stack
        spacing={3.5}
        sx={{ maxWidth: MAX_WIDTH, mx: "auto", userSelect: "none" }}
      >
        {/* who is here, what there is, and the two things to start with */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            columnGap: 3.5,
            rowGap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, letterSpacing: "-.015em" }}
            >
              {greeting}
            </Typography>
            <Typography
              sx={{ mt: 0.75, color: "text.secondary", maxWidth: "70ch" }}
            >
              {summary.total === 0 ? (
                t("noCases")
              ) : (
                <Trans
                  i18nKey="home.cases"
                  count={summary.total}
                  values={{ open: summary.open }}
                  components={{ bold }}
                />
              )}
              {latest?.updatedAt && (
                <>
                  {" "}
                  <Trans
                    i18nKey="home.lastChange"
                    values={{
                      when: ago(latest.updatedAt),
                      title: latest.title,
                    }}
                    components={{
                      case: (
                        <MuiLink
                          component={Link}
                          to={`/cases/${latest.id}`}
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                        />
                      ),
                    }}
                  />
                </>
              )}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setImportOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("import")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/addCase")}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("newCase")}
            </Button>
          </Stack>
        </Box>

        {summary.total > 0 && (
          <>
            {/* working on the cases: everything here leads to a case or to the
              list of the cases */}
            <Box
              component="section"
              aria-labelledby="cases"
              sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}
            >
              <div>
                <SectionHeading id="cases">
                  {t("section.cases.title")}
                </SectionHeading>
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, color: "text.secondary" }}
                >
                  {t("casesArea")}
                </Typography>
              </div>

              {/* straight to a case: the list that is loaded is searched */}
              <Autocomplete
                options={cases}
                value={null}
                inputValue={search}
                onInputChange={(_, value) => setSearch(value)}
                // the cases are offered once something is typed
                open={search.trim().length > 0}
                getOptionLabel={(c) => c.title}
                getOptionKey={(c) => c.id}
                filterOptions={(options, { inputValue }) => {
                  const term = inputValue.trim().toLowerCase();
                  return options.filter((c) =>
                    [c.title, ...c.parties].some((text) =>
                      text.toLowerCase().includes(term),
                    ),
                  );
                }}
                noOptionsText={t("noMatch")}
                onChange={(_, c) => c && navigate(`/cases/${c.id}`)}
                renderOption={({ key, ...props }, c) => (
                  <li key={key} {...props}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ display: "block", color: "text.secondary" }}
                      >
                        {c.parties.join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("search")}
                    slotProps={{
                      ...params.slotProps,
                      htmlInput: {
                        ...params.slotProps.htmlInput,
                        "aria-label": t("search"),
                      },
                      input: {
                        ...params.slotProps.input,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                          </InputAdornment>
                        ),
                        sx: { borderRadius: 2.5, bgcolor: "background.paper" },
                      },
                    }}
                  />
                )}
              />

              {/* the cases in four figures */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 1.75,
                }}
              >
                {figures.map(({ label, value, note, to }) => (
                  <Paper
                    key={label}
                    variant="outlined"
                    {...(to ? { component: Link, to } : {})}
                    sx={{
                      ...card,
                      px: 2.25,
                      pt: 1.75,
                      pb: 2,
                      ...(to && {
                        ...plainLink,
                        display: "block",
                        "&:hover": { bgcolor: "action.hover" },
                      }),
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="h4"
                      component="p"
                      sx={{
                        fontWeight: 700,
                        letterSpacing: "-.02em",
                        lineHeight: 1.2,
                        ...TABULAR_NUMERALS,
                      }}
                    >
                      {value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {note}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  gap: 2.25,
                }}
              >
                {/* the cases changed last, to carry on with */}
                <Paper
                  component="section"
                  variant="outlined"
                  sx={{
                    ...card,
                    flex: "1.6 1 560px",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <SectionLabel component="h2">
                      {t("recent.title")}
                    </SectionLabel>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: "text.secondary" }}
                    >
                      {t("recent.description")}
                    </Typography>
                  </Box>
                  {summary.recent.map((c) => (
                    <Box
                      key={c.id}
                      component={Link}
                      to={`/cases/${c.id}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        columnGap: 2.25,
                        alignItems: "center",
                        px: 2.5,
                        py: 1.5,
                        borderTop: 1,
                        borderColor: "divider",
                        ...plainLink,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            {c.title}
                          </Typography>
                          {Object.keys(c.claimTypeCounts).map((type) => (
                            <Box
                              key={type}
                              component="span"
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1.25,
                                typography: "caption",
                                fontWeight: 600,
                                ...getClaimTypeChipStyle(type, theme),
                              }}
                            >
                              {t(`claimTypes.${type}`)}
                            </Box>
                          ))}
                          {c.settlementStatus !== "none" && (
                            <TonalBadge
                              tone={
                                c.settlementStatus === "full"
                                  ? theme.palette.data.positive
                                  : theme.palette.primary
                              }
                            >
                              {t(`settlement.badge.${c.settlementStatus}`, {
                                keyPrefix: "CasesList",
                              })}
                            </TonalBadge>
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ mt: 0.375, color: "text.secondary" }}
                        >
                          {c.clientParty && (
                            <>
                              <Box
                                component="span"
                                sx={{ fontWeight: 600, color: "text.primary" }}
                              >
                                {t("partyClient", {
                                  keyPrefix: "CasesList",
                                  party: c.clientParty,
                                })}
                              </Box>
                              {others(c) && " · "}
                            </>
                          )}
                          {others(c)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.875,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: getStageColor(
                                c.currentStage ?? Stage.PreAction,
                                theme,
                              ),
                            }}
                          />
                          {t(
                            `stages.${Stage[c.currentStage ?? Stage.PreAction]}`,
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            minWidth: 84,
                            textAlign: "right",
                            color: "text.secondary",
                          }}
                        >
                          {c.updatedAt && ago(c.updatedAt)}
                        </Typography>
                        <ChevronIcon
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      </Box>
                    </Box>
                  ))}
                  <Box
                    component={Link}
                    to={CASES_PATH}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.75,
                      py: 1.375,
                      borderTop: 1,
                      borderColor: "divider",
                      bgcolor: "background.default",
                      typography: "body2",
                      fontWeight: 600,
                      color: "primary.main",
                      textDecoration: "none",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {t("recent.all", { count: summary.total })}
                    <ArrowIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Paper>

                {/* how far the cases have come, and how many are settled */}
                <Paper
                  component="section"
                  variant="outlined"
                  sx={{
                    ...card,
                    flex: "1 1 340px",
                    minWidth: 0,
                    px: 2.5,
                    pt: 2,
                    pb: 2.25,
                  }}
                >
                  <SectionLabel component="h2">
                    {t("standing.title")}
                  </SectionLabel>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, mb: 1.5, color: "text.secondary" }}
                  >
                    {summary.open > 0
                      ? t("standing.description", { count: summary.open })
                      : t("standing.allSettled")}
                  </Typography>
                  {summary.open > 0 && (
                    <Stack spacing={0.625}>
                      {summary.stages.map(({ stage, count }) => (
                        <CountRow
                          key={stage}
                          label={t(`stages.${Stage[stage]}`)}
                          count={count}
                          most={mostPerStage}
                          color={theme.palette.primary.main}
                          barWidth="minmax(0, 1.2fr)"
                          barHeight={12}
                        />
                      ))}
                    </Stack>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 2.25,
                      mb: 0.75,
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {t("standing.settlement")}
                  </Typography>
                  <SplitBar
                    parts={settlementParts.map(({ key, count, color, to }) => ({
                      key,
                      label: t(`standing.${key}`),
                      share: count / summary.total,
                      shareText: String(count),
                      color,
                      ink: theme.palette.text.primary,
                      to,
                    }))}
                  />
                </Paper>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: 2.25,
                  alignItems: "start",
                }}
              >
                {/* what the cases are about: each type leads to its cases */}
                <Paper
                  component="section"
                  variant="outlined"
                  sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}
                >
                  <SectionLabel component="h2">{t("types.title")}</SectionLabel>
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {shownTypes.map(({ type, count }) => {
                      const color = getClaimTypeChipStyle(
                        type,
                        theme,
                      ).backgroundColor;
                      const label = t(`claimTypes.${type}`);
                      return (
                        <CountRow
                          key={type}
                          label={
                            <>
                              <Box
                                component="span"
                                sx={{
                                  flex: "none",
                                  width: 10,
                                  height: 10,
                                  borderRadius: 0.75,
                                  bgcolor: color,
                                }}
                              />
                              <Box component="span" sx={{ minWidth: 0 }}>
                                {label}
                              </Box>
                            </>
                          }
                          count={count}
                          most={shownTypes[0].count}
                          color={color}
                          to={`/cases?claimType=${encodeURIComponent(type)}`}
                          title={t("types.show", { type: label })}
                        />
                      );
                    })}
                  </Stack>
                  {moreTypes > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1.25,
                        color: "text.secondary",
                      }}
                    >
                      {t("types.more", { count: moreTypes })}
                    </Typography>
                  )}
                </Paper>

                {/* how the cases are funded: each agreement leads to its cases */}
                <Paper
                  component="section"
                  variant="outlined"
                  sx={{ ...card, px: 2.5, pt: 2, pb: 2.25 }}
                >
                  <SectionLabel component="h2">
                    {t("funding.title")}
                  </SectionLabel>
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {summary.funding.map(({ type, count }) => {
                      const label =
                        type === NO_FUNDING
                          ? t("funding.none")
                          : t(`agreementTypes.${type}`);
                      return (
                        <CountRow
                          key={type}
                          label={label}
                          count={count}
                          most={mostFunding}
                          color={lighten(
                            theme.palette.primary.main,
                            type === NO_FUNDING
                              ? NO_FUNDING_LIGHTEN
                              : FUNDING_LIGHTEN,
                          )}
                          to={`/cases?funding=${type}`}
                          title={t("funding.show", { type: label })}
                        />
                      );
                    })}
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1.25, color: "text.secondary" }}
                  >
                    {t("funding.note")}
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <PortfolioArea overview={overview} total={summary.total} />
          </>
        )}

        {/* the foot of the page, apart from both areas: what users say, the
            quietest part of the page, and the copyright */}
        <Box
          component="footer"
          sx={{
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            textAlign: "center",
          }}
        >
          {quote && (
            <>
              <Typography variant="body2">“{quote.text}”</Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  columnGap: 1.5,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {quote.author}
                </Typography>
                <MuiLink
                  component={Link}
                  to="/testimonials"
                  underline="hover"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    typography: "body2",
                    fontWeight: 600,
                  }}
                >
                  {t("testimonials.more")}
                  <ArrowIcon sx={{ fontSize: 15 }} />
                </MuiLink>
              </Box>
            </>
          )}
          <Box sx={{ mt: quote ? 2.25 : 0 }}>
            <Copyright />
          </Box>
        </Box>
      </Stack>
      {importOpen && (
        <ImportCaseDialog open onClose={() => setImportOpen(false)} />
      )}
    </Box>
  );
};
